import { useRef, useState } from 'react'
import { Button } from 'blaze-ui/Button/Button'
import { Edit1, ChevronRightSmall, Trash2 } from 'blaze-ui/icons'
import { Heading } from 'blaze-ui/Heading/Heading'
import { ProseInput } from 'common/ProseInput/ProseInput'
import { ProseDisplay } from 'common/ProseInput/ProseDisplay'
import {
  convertMarkdownToHtml,
  convertHtmlToMarkdown,
  ProfileSection,
} from 'screens/BrandKits/components/BrandProfile/markdownUtils'
import { brandProfileSchema } from 'screens/BrandKits/components/BrandProfile/brandProfileSchema'
import { Pressable, useInteractOutside } from '@react-aria/interactions'
import { ResponsiveDesign } from 'screens/blaze/Home/components/ResponsiveDesign'
import useResponsiveDesign from 'hooks/useResponsiveDesign'
import { useRootStore } from 'stores/RootStore'
import { EditableSectionDrawer } from './EditableSectionDrawer'
import cx from 'classnames'

interface EditableSectionProps {
  section: ProfileSection
  sectionKey: string
  onSectionUpdate: (sectionKey: string, newContent: string) => void
  onSectionDelete?: (sectionKey: string) => void
  isUpdating: boolean
  isEditing: boolean
  onEdit?: () => void
  onCancel?: () => void
  isDisabled?: boolean
  showDeleteButton?: boolean
  className?: string
  updateOnChange?: boolean
  saveButtonText?: string
  sectionClassName?: string
  headerClassName?: string
  actionsClassName?: string
  editButtonClassName?: string
  deleteButtonClassName?: string
  contentClassName?: string
  editingClassName?: string
  editorClassName?: string
  isCollapsed?: boolean
  onToggle?: () => void
  editInline?: boolean
}

export const EditableSection = ({
  section,
  sectionKey,
  onSectionUpdate,
  onSectionDelete,
  isUpdating,
  isEditing,
  onEdit,
  onCancel,
  isDisabled,
  showDeleteButton = false,
  updateOnChange = false,
  saveButtonText = 'Save',
  className = '',
  sectionClassName = '',
  headerClassName = '',
  actionsClassName = '',
  editButtonClassName = '',
  deleteButtonClassName = '',
  contentClassName = '',
  editingClassName = '',
  editorClassName = '',
  isCollapsed = false,
  onToggle,
  editInline = false,
}: EditableSectionProps) => {
  const [originalHtmlContent, setOriginalHtmlContent] = useState(
    editInline ? convertMarkdownToHtml(section?.content ?? '', brandProfileSchema) : '',
  )
  const [currentHtmlContent, setCurrentHtmlContent] = useState(
    editInline ? convertMarkdownToHtml(section?.content ?? '', brandProfileSchema) : '',
  )
  const editableSectionRef = useRef<HTMLDivElement>(null)
  const { isMobile, isIOSNative } = useResponsiveDesign()
  const { drawersStore } = useRootStore()

  const handleEdit = () => {
    const htmlContent = convertMarkdownToHtml(section?.content ?? '', brandProfileSchema)
    setOriginalHtmlContent(htmlContent)
    setCurrentHtmlContent(htmlContent)
    onEdit?.()
  }

  const handleUpdate = (html?: string) => {
    const sourceHtml = html ?? currentHtmlContent
    // Only update if content actually changed
    if (sourceHtml === originalHtmlContent) {
      return
    }
    const markdownContent = convertHtmlToMarkdown(sourceHtml, brandProfileSchema)
    onSectionUpdate(sectionKey, markdownContent)
  }

  const handleSave = () => {
    handleUpdate()
    onCancel?.()
  }

  const handleCancel = () => {
    onCancel?.()
  }

  const handleDelete = () => {
    if (onSectionDelete) {
      onSectionDelete(sectionKey)
    }
  }

  const handleContentChange = (htmlContent: string) => {
    setCurrentHtmlContent(htmlContent)
    if (updateOnChange) {
      handleUpdate(htmlContent)
    }
  }

  const handleMobileContentClick = () => {
    if (isEditing || isDisabled) return
    if (updateOnChange && (isMobile || isIOSNative)) {
      handleEdit()
    } else if (isMobile) {
      drawersStore.openDrawer(EditableSectionDrawer, {
        section,
        sectionKey,
        onSectionUpdate,
        isUpdating,
      })
    }
  }

  const handleClickOutside = () => {
    if (!isEditing || isUpdating) return

    handleSave()
  }

  useInteractOutside({ ref: editableSectionRef, onInteractOutside: handleClickOutside })

  const showAccordion = isMobile && !editInline

  if (section.content.length === 0) return null

  return (
    <div className={`${className} ${sectionClassName}`.trim()} ref={editableSectionRef}>
      <div
        className={`${headerClassName}`.trim()}
        onClick={showAccordion ? onToggle : undefined}
        style={{ cursor: showAccordion ? 'pointer' : undefined }}
      >
        <Heading level={3}>{section.title}</Heading>

        {showAccordion && (
          <ResponsiveDesign.Mobile>
            <div
              style={{
                transform: isCollapsed ? 'rotate(0deg)' : 'rotate(90deg)',
                transition: 'transform 0.2s ease',
                display: 'flex',
                alignItems: 'center',
              }}
            >
              <ChevronRightSmall />
            </div>
          </ResponsiveDesign.Mobile>
        )}

        {!editInline && (
          <ResponsiveDesign.Desktop>
            {isEditing ? (
              <div className={`${actionsClassName}`.trim()}>
                <Button variant="editor" size="sm" onPress={handleCancel} isDisabled={isUpdating}>
                  Cancel
                </Button>
                <Button variant="ghost" size="sm" onPress={handleSave} isDisabled={isUpdating}>
                  {isUpdating ? 'Saving...' : saveButtonText}
                </Button>
              </div>
            ) : (
              <div className={`${actionsClassName}`.trim()}>
                {showDeleteButton && (
                  <Button
                    variant="ghost"
                    size="sm"
                    frontIcon={Trash2}
                    onPress={handleDelete}
                    className={deleteButtonClassName}
                    isDisabled={isDisabled}
                  >
                    Delete
                  </Button>
                )}
                <Button
                  variant="ghost"
                  size="sm"
                  frontIcon={Edit1}
                  onPress={handleEdit}
                  className={editButtonClassName}
                  isDisabled={isDisabled}
                >
                  Edit
                </Button>
              </div>
            )}
          </ResponsiveDesign.Desktop>
        )}
      </div>

      {!isCollapsed && (
        <>
          {isEditing ? (
            <div className={`${editingClassName}`.trim()} data-section-content>
              <ProseInput
                initialHtmlString={currentHtmlContent}
                onChange={handleContentChange}
                placeholder="Enter section content..."
                className={editorClassName}
                schema={brandProfileSchema}
                autoFocus={isIOSNative}
              />
            </div>
          ) : (
            <Pressable onPress={handleMobileContentClick}>
              <div className={cx(contentClassName, { collapsed: isCollapsed })} data-section-content>
                <ProseDisplay
                  html={convertMarkdownToHtml(section.content, brandProfileSchema)}
                  schema={brandProfileSchema}
                />
              </div>
            </Pressable>
          )}
        </>
      )}
    </div>
  )
}
